import React, { useState, useEffect } from 'react';
import { validateRegulatoryLink, sanitizeRegulatoryLink } from '../utils/regulatoryLinkValidator.ts';
import './RegulatoryLinkInput.css';

interface RegulatoryLinkInputProps {
  value: string | null | undefined;
  onChange: (link: string | null) => void;
  disabled?: boolean;
}

const RegulatoryLinkInput: React.FC<RegulatoryLinkInputProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  // Update input when external value changes
  useEffect(() => {
    setInputValue(value || '');
    if (value) {
      const validation = validateRegulatoryLink(value);
      setIsValid(validation.isValid);
      setError(validation.isValid ? null : validation.error || null);
    } else {
      setIsValid(false);
      setError(null);
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (!newValue.trim()) {
      // Empty input is valid (optional field)
      setError(null);
      setIsValid(false);
      onChange(null);
      return;
    }

    // Sanitize and validate
    const sanitized = sanitizeRegulatoryLink(newValue);
    const validation = validateRegulatoryLink(sanitized);

    setError(validation.error || null);
    setIsValid(validation.isValid);

    if (validation.isValid) {
      onChange(sanitized);
    } else {
      onChange(null);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setError(null);
    setIsValid(false);
    onChange(null);
  };

  return (
    <div className="regulatory-link-input">
      <div className="regulatory-link-header">
        <label className="regulatory-link-label">
          🔗 External URL (Optional)
        </label>
      </div>

      <div className="regulatory-link-description">
        Add an external link for this banner (opens in a new tab)
      </div>

      <div className={`regulatory-link-input-group ${error ? 'has-error' : ''} ${isValid ? 'has-success' : ''}`}>
        <input
          type="url"
          className="regulatory-link-text-input"
          value={inputValue}
          onChange={handleInputChange}
          placeholder="https://example.com"
          disabled={disabled}
        />
        {inputValue && !disabled && (
          <button
            type="button"
            className="regulatory-link-clear-button"
            onClick={handleClear}
            title="Clear link"
          >
            ✕
          </button>
        )}
        {isValid && (
          <span className="regulatory-link-valid-icon" title="Valid URL">
            ✓
          </span>
        )}
      </div>

      {error && (
        <div className="regulatory-link-error">
          {error}
        </div>
      )}

      {isValid && inputValue && (() => {
        const sanitized = sanitizeRegulatoryLink(inputValue);
        return (
          <div className="regulatory-link-preview">
            <strong>Preview:</strong>{' '}
            <a
              href={sanitized}
              target="_blank"
              rel="noopener noreferrer"
              className="regulatory-link-preview-link"
            >
              {sanitized}
            </a>
          </div>
        );
      })()}
    </div>
  );
};

export default RegulatoryLinkInput;
